import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, extractErrorMessage } from '@kardume/services';
import type { EmailChannelStatusDto } from '@kardume/types';
import { Badge, Button, TextInput } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';

const Panel = styled.section`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  background: ${(p) => p.theme.surface};
  margin-bottom: 1.25rem;
  overflow: hidden;
`;

const Head = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${(p) => p.theme.textPrimary};
  font-weight: 700;
  font-size: 0.9rem;
  text-align: left;

  .spacer {
    margin-left: auto;
  }
`;

const Body = styled.div`
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Grid = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;

  dt {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${(p) => p.theme.textMuted};
  }
  dd {
    margin: 0.15rem 0 0;
    font-size: 0.86rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-all;
  }
`;

const Warning = styled.p`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin: 0;
  padding: 0.65rem 0.8rem;
  border-radius: ${(p) => p.theme.radius};
  font-size: 0.85rem;
  color: ${(p) => p.theme.warning};
  background: ${(p) => p.theme.warning}14;
`;

const Ok = styled.p`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin: 0;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textMuted};
`;

const TestRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > *:first-child {
    flex: 1;
    min-width: 220px;
  }
`;

/**
 * Diagnóstico do canal de e-mail, na tela onde o sintoma aparece.
 *
 * Um convite que não chega é indistinguível de um que chegou, do lado de cá. Este
 * painel responde às duas perguntas que importam antes de culpar o servidor: o
 * canal está mesmo ligado, e o remetente confere com a conta autenticada no SMTP -
 * a divergência entre os dois é recusada pelo provedor e nunca vira erro aqui.
 *
 * O envio de teste é o passo final: ele devolve a recusa real do SMTP em vez de
 * exigir acesso ao log da aplicação.
 */
const EmailChannelPanel: React.FC = () => {
  const { showNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const { data, loading, reload } = useAsync<EmailChannelStatusDto>(() => backofficeApi.getEmailStatus(), []);

  const handleTest = async () => {
    if (!to || sending) return;
    setSending(true);
    try {
      await backofficeApi.sendTestEmail(to.trim());
      showNotification(`E-mail de teste enviado para ${to.trim()}.`, 'success');
    } catch (err) {
      // A mensagem do backend traz a recusa do provedor (autenticação, remetente,
      // conexão). É exatamente o que falta para diagnosticar sem abrir o log.
      showNotification(extractErrorMessage(err, 'Falha no envio de teste.'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel>
      <Head type="button" onClick={() => setOpen((v) => !v)}>
        <Mail size={16} />
        Canal de e-mail
        {!loading && data && (
          <Badge tone={data.enabled && !data.warning ? 'success' : 'danger'}>
            {data.enabled ? (data.warning ? 'Atenção' : 'Ativo') : 'Desligado'}
          </Badge>
        )}
        <span className="spacer" />
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </Head>

      {open && (
        <Body>
          {loading && <span>Carregando…</span>}
          {data && (
            <>
              {data.warning ? (
                <Warning>
                  <AlertTriangle size={16} />
                  <span>{data.warning}</span>
                </Warning>
              ) : (
                <Ok>
                  <CheckCircle2 size={16} />
                  <span>Configuração sem pendências. Use o teste abaixo para confirmar a entrega.</span>
                </Ok>
              )}

              <Grid>
                <div>
                  <dt>Servidor</dt>
                  <dd>{data.host || '-'}{data.port ? `:${data.port}` : ''}</dd>
                </div>
                <div>
                  <dt>Conta autenticada</dt>
                  <dd>{data.username || '-'}</dd>
                </div>
                <div>
                  <dt>Remetente</dt>
                  <dd>{data.from || '-'}</dd>
                </div>
                <div>
                  <dt>Link do backoffice</dt>
                  <dd>{data.backofficeUrl}</dd>
                </div>
              </Grid>

              <TestRow>
                <TextInput
                  label="Enviar teste para"
                  type="email"
                  placeholder="voce@kardumeurbano.com.br"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  disabled={sending || !data.enabled}
                />
                <Button
                  type="button"
                  variant="secondary"
                  isLoading={sending}
                  disabled={!to || !data.enabled}
                  onClick={handleTest}
                >
                  Enviar teste
                </Button>
                <Button type="button" variant="ghost" onClick={reload} disabled={sending}>
                  Recarregar
                </Button>
              </TestRow>
            </>
          )}
        </Body>
      )}
    </Panel>
  );
};

export default EmailChannelPanel;
